import axios from "src/utils/axios";
import { createSlice } from "@reduxjs/toolkit";

// ----------------------------------------------------------------------
//
//   Slice del Hub de Integraciones (bloque E onboarding v2). Fuente
//   de datos común para las 4 tabs (Hub · Catálogo · Detalle · Reglas)
//   y el wizard genérico de conexión.
//
//   Estado plano — no normalizamos entities: cada tab solo consume
//   1-2 colecciones a la vez y el volumen es bajo (< 20 conexiones
//   por restaurante realista).
//
// ----------------------------------------------------------------------

const initialState = {
  loading: false,
  error: null,

  // Tab Hub
  hub: null,                    // { connectedCount, availableCount, healthPercent, connections[], recommendations[], recentActivity[] }

  // Tab Catálogo
  catalog: [],                  // DtoIntegrationProvider[]
  catalogLoading: false,

  // Tab Detalle (activo por providerCode)
  detail: null,                 // DtoIntegrationDetail
  detailLoading: false,

  // Wizard genérico
  wizard: {
    open: false,
    providerCode: null,
    step: 1,                    // 1..6
    // Datos acumulados de cada paso
    authStarted: null,          // { redirectUrl, state }
    connectionId: null,         // devuelto tras CompleteAuth
    permissions: [],
    linkables: [],
    selectedLinkable: null,     // { externalId, externalName }
    testReport: null,           // { allPassed, checks[] }
    error: null,
    running: false,
  },

  // Fix6 · Códigos de providers con conexión ACTIVA. El nav los consume
  // para decidir qué items dependientes de integración mostrar
  // (ej. "Reseñas · PRO" solo si activeCodes.includes("google")).
  activeCodes: [],
  activeCodesLoaded: false,
};

const slice = createSlice({
  name: "integrations",
  initialState,
  reducers: {
    startLoading(state) { state.loading = true; state.error = null; },
    stopLoading(state) { state.loading = false; },
    hasError(state, action) { state.loading = false; state.error = action.payload; },

    setHub(state, action) { state.hub = action.payload; state.loading = false; },
    setCatalog(state, action) { state.catalog = action.payload; state.catalogLoading = false; },
    setCatalogLoading(state, action) { state.catalogLoading = action.payload; },

    setDetail(state, action) { state.detail = action.payload; state.detailLoading = false; },
    setDetailLoading(state, action) { state.detailLoading = action.payload; },
    clearDetail(state) { state.detail = null; },

    openWizard(state, action) {
      state.wizard = { ...initialState.wizard, open: true, providerCode: action.payload };
    },
    closeWizard(state) { state.wizard = { ...initialState.wizard }; },
    setWizardStep(state, action) { state.wizard.step = action.payload; },
    setWizardRunning(state, action) { state.wizard.running = action.payload; },
    setWizardError(state, action) { state.wizard.error = action.payload; state.wizard.running = false; },
    setWizardAuthStarted(state, action) { state.wizard.authStarted = action.payload; },
    setWizardConnectionId(state, action) { state.wizard.connectionId = action.payload; },
    setWizardPermissions(state, action) { state.wizard.permissions = action.payload; },
    setWizardLinkables(state, action) { state.wizard.linkables = action.payload; },
    setWizardSelectedLinkable(state, action) { state.wizard.selectedLinkable = action.payload; },
    setWizardTestReport(state, action) { state.wizard.testReport = action.payload; },

    setActiveCodes(state, action) {
      state.activeCodes = action.payload || [];
      state.activeCodesLoaded = true;
    },
  },
});

export default slice.reducer;
export const {
  openWizard,
  closeWizard,
  setWizardStep,
} = slice.actions;

// ----------------------------------------------------------------------
//   Thunks
// ----------------------------------------------------------------------

export function fetchHub() {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.startLoading());
      const res = await axios.get("integrations");
      dispatch(slice.actions.setHub(res.data));
    } catch (err) {
      dispatch(slice.actions.hasError(err?.response?.data?.message || err.message));
    }
  };
}

export function fetchCatalog() {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.setCatalogLoading(true));
      const res = await axios.get("integrations/catalog");
      dispatch(slice.actions.setCatalog(res.data));
    } catch (err) {
      dispatch(slice.actions.hasError(err?.response?.data?.message || err.message));
      dispatch(slice.actions.setCatalogLoading(false));
    }
  };
}

export function fetchDetail(providerCode) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.setDetailLoading(true));
      const res = await axios.get(`integrations/${providerCode}/detail`);
      dispatch(slice.actions.setDetail(res.data));
    } catch (err) {
      dispatch(slice.actions.hasError(err?.response?.data?.message || err.message));
      dispatch(slice.actions.setDetailLoading(false));
    }
  };
}

// ─── Wizard flow ───────────────────────────────────────────────────────

/** Paso 2: arranca OAuth y navega/embed según provider. */
export function wizardStartAuth(providerCode) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.setWizardRunning(true));
      const res = await axios.post(`integrations/${providerCode}/auth/start`);
      dispatch(slice.actions.setWizardAuthStarted(res.data));
      dispatch(slice.actions.setWizardRunning(false));
      return res.data;
    } catch (err) {
      dispatch(slice.actions.setWizardError(err?.response?.data?.message || err.message));
    }
  };
}

/** Paso 3: permisos. Cachea entre re-entradas al paso. */
export function wizardFetchPermissions(providerCode) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.setWizardRunning(true));
      const res = await axios.get(`integrations/${providerCode}/permissions`);
      dispatch(slice.actions.setWizardPermissions(res.data));
      dispatch(slice.actions.setWizardRunning(false));
    } catch (err) {
      dispatch(slice.actions.setWizardError(err?.response?.data?.message || err.message));
    }
  };
}

/** Paso 4: linkables (locales/cuentas). */
export function wizardFetchLinkables(providerCode) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.setWizardRunning(true));
      const res = await axios.get(`integrations/${providerCode}/linkables`);
      dispatch(slice.actions.setWizardLinkables(res.data));
      // Pre-selección: el de match más alto (>= 80).
      if (res.data?.length) {
        const top = res.data[0];
        if (top.matchScore >= 80) {
          dispatch(slice.actions.setWizardSelectedLinkable({
            externalId: top.externalId,
            externalName: top.name,
          }));
        }
      }
      dispatch(slice.actions.setWizardRunning(false));
    } catch (err) {
      dispatch(slice.actions.setWizardError(err?.response?.data?.message || err.message));
    }
  };
}

/** Paso 4 confirm. */
export function wizardLink(providerCode, externalId, externalName) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.setWizardRunning(true));
      await axios.post(`integrations/${providerCode}/link`, { externalId, externalName });
      dispatch(slice.actions.setWizardSelectedLinkable({ externalId, externalName }));
      dispatch(slice.actions.setWizardRunning(false));
      // 2026-08-24 · Nueva conexión activa → refresca cache de codes.
      dispatch(fetchActiveIntegrationCodes({ force: true }));
    } catch (err) {
      dispatch(slice.actions.setWizardError(err?.response?.data?.message || err.message));
    }
  };
}

/** Paso 5: batería de checks. */
export function wizardRunTest(providerCode) {
  return async (dispatch) => {
    try {
      dispatch(slice.actions.setWizardRunning(true));
      const res = await axios.post(`integrations/${providerCode}/test`);
      dispatch(slice.actions.setWizardTestReport(res.data));
      dispatch(slice.actions.setWizardRunning(false));
    } catch (err) {
      dispatch(slice.actions.setWizardError(err?.response?.data?.message || err.message));
    }
  };
}

// ─── Zona de peligro ───────────────────────────────────────────────────

export function pauseIntegration(providerCode) {
  return async (dispatch) => {
    await axios.post(`integrations/${providerCode}/pause`);
    dispatch(fetchDetail(providerCode));
    dispatch(fetchHub());
    dispatch(fetchActiveIntegrationCodes({ force: true }));
  };
}

export function resumeIntegration(providerCode) {
  return async (dispatch) => {
    await axios.post(`integrations/${providerCode}/resume`);
    dispatch(fetchDetail(providerCode));
    dispatch(fetchHub());
    dispatch(fetchActiveIntegrationCodes({ force: true }));
  };
}

export function disconnectIntegration(providerCode) {
  return async (dispatch) => {
    await axios.post(`integrations/${providerCode}/disconnect`);
    dispatch(slice.actions.clearDetail());
    dispatch(fetchHub());
    dispatch(fetchCatalog());
    // 2026-08-24 · Invalida el cache de activeCodes — el nav debe
    // reflejar la baja al instante (KDS, Cajón, Reseñas… vuelven a
    // pintar "Contrátalo" tras desconectar).
    dispatch(fetchActiveIntegrationCodes({ force: true }));
  };
}

/** Providers coming_soon: registrar interés. */
export function registerInterest(providerCode) {
  return async () => {
    await axios.post(`integrations/${providerCode}/interest`);
  };
}

/**
 * Fix6 · Fetch de los códigos de providers con conexión activa. Se llama
 * al montar el DashboardLayout — el nav lo consume para decidir qué
 * items dependientes de integración mostrar. Silencioso: si falla, deja
 * `activeCodes=[]` (nav no muestra los items dependientes).
 *
 * 2026-08-24 · Cachea a nivel slice (`activeCodesLoaded`). Pasa
 * `{force:true}` para invalidar (ej. tras conectar/desconectar un
 * provider). Sin este cache el TPV disparaba una request a
 * `/integrations/connections/active-codes` cada vez que se abría o
 * cerraba una mesa — datos que solo cambian cuando el user toca
 * ajustes de integraciones (evento raro).
 */
export function fetchActiveIntegrationCodes({ force = false } = {}) {
  return async (dispatch, getState) => {
    if (!force) {
      const st = getState().integrations;
      if (st?.activeCodesLoaded) return;
    }
    try {
      const res = await axios.get("integrations/connections/active-codes");
      dispatch(slice.actions.setActiveCodes(res.data || []));
    } catch {
      dispatch(slice.actions.setActiveCodes([]));
    }
  };
}

// ─── Reglas SI/ENTONCES (bloque E5) ───────────────────────────────────

export function fetchRules(domain = "reviews") {
  return async () => {
    const res = await axios.get(`integrations/rules?domain=${encodeURIComponent(domain)}`);
    return res.data;
  };
}

export function upsertRule(dto) {
  return async () => {
    const res = dto.id
      ? await axios.put(`integrations/rules/${dto.id}`, dto)
      : await axios.post(`integrations/rules`, dto);
    return res.data;
  };
}

export function deleteRule(id) {
  return async () => {
    await axios.delete(`integrations/rules/${id}`);
  };
}

export function toggleRule(id, enabled) {
  return async () => {
    await axios.post(`integrations/rules/${id}/toggle?enabled=${enabled}`);
  };
}
